import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Copy, ArrowSquareOut, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const PROVIDER_META = {
  google: {
    label: "Google",
    console_url: "https://console.cloud.google.com/apis/credentials",
    env_keys: ["GOOGLE_CLIENT_ID", "GOOGLE_CLIENT_SECRET"],
    steps: [
      "Open Google Cloud Console → APIs & Services → Credentials.",
      "Enable the Gmail API and Google Calendar API for your project.",
      "Click Create Credentials → OAuth client ID → Application type: Web application.",
      "Under Authorized redirect URIs, paste the URI shown below.",
      "Copy the resulting Client ID and Client Secret into your backend .env file.",
    ],
    scopes_hint: "Requested scopes: openid, email, gmail.readonly, calendar.readonly (offline access).",
  },
  microsoft: {
    label: "Microsoft (Outlook / M365)",
    console_url: "https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade",
    env_keys: ["MS_CLIENT_ID", "MS_CLIENT_SECRET"],
    steps: [
      "Open Azure Portal → Azure Active Directory → App registrations → New registration.",
      "Supported account types: Accounts in any organizational directory and personal Microsoft accounts.",
      "Under Redirect URI (Web), paste the URI shown below.",
      "After the app is created, go to Certificates & secrets → New client secret → copy the Value.",
      "In API permissions, add Microsoft Graph delegated permissions: Mail.Read, Calendars.Read, offline_access. Click Grant admin consent.",
      "Copy the Application (client) ID and secret Value into your backend .env file.",
    ],
    scopes_hint: "Requested scopes: openid, email, offline_access, Mail.Read, Calendars.Read.",
  },
  slack: {
    label: "Slack",
    console_url: "https://api.slack.com/apps",
    env_keys: ["SLACK_CLIENT_ID", "SLACK_CLIENT_SECRET"],
    steps: [
      "Open api.slack.com/apps → Create New App → From scratch.",
      "Go to OAuth & Permissions → add the redirect URI shown below.",
      "Under Scopes → Bot Token Scopes, add: channels:history, channels:read, chat:write, users:read.",
      "Install the app to your workspace to activate the credentials.",
      "Copy the Client ID and Client Secret from Basic Information into your backend .env file.",
    ],
    scopes_hint: "Requested bot scopes: channels:history, channels:read, chat:write, users:read.",
  },
  hubspot: {
    label: "HubSpot",
    console_url: "https://developers.hubspot.com/",
    env_keys: ["HUBSPOT_CLIENT_ID", "HUBSPOT_CLIENT_SECRET"],
    steps: [
      "Open developers.hubspot.com → Manage apps → Create app.",
      "In the Auth tab, add the redirect URL shown below.",
      "Add these scopes: crm.objects.contacts.read, crm.objects.contacts.write, crm.objects.deals.read, oauth.",
      "Copy the Client ID and Client Secret from the Auth tab into your backend .env file.",
    ],
    scopes_hint: "Requested scopes: crm.objects.contacts.read/write, crm.objects.deals.read, oauth.",
  },
};

function CopyButton({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          toast.success("Copied");
          setTimeout(() => setCopied(false), 1500);
        } catch { toast.error("Copy failed"); }
      }}
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono px-2 py-1 border border-stone-700 hover:border-orange-600 text-stone-300 hover:text-orange-500 transition-colors"
    >
      {copied ? <Check size={12} weight="bold" /> : <Copy size={12} weight="regular" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export default function SetupCredentialsDialog({ open, onOpenChange, integration, provider, backendUrl, onRetry }) {
  const meta = provider ? PROVIDER_META[provider] : null;
  if (!meta) return null;

  const redirectUri = `${backendUrl}/api/oauth/${integration}/callback`;
  const envSnippet = meta.env_keys.map(k => `${k}=`).join("\n");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="setup-wizard-dialog"
        className="max-w-2xl bg-[#1c1917] border border-stone-800 text-stone-100 rounded-none p-0 gap-0"
      >
        <DialogHeader className="px-8 pt-8 pb-6 border-b border-stone-800">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-orange-500/80">Connect a provider</div>
          <DialogTitle asChild>
            <h2 className="font-serif text-3xl mt-2 tracking-tight text-stone-50" data-testid="wizard-title">
              Set up {meta.label} OAuth
            </h2>
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-400 mt-2 leading-relaxed">
            Copilot needs an OAuth app on {meta.label} before it can talk to your data. This is a one-time setup — after credentials are in place, every user who clicks Connect will complete the flow in seconds.
          </DialogDescription>
        </DialogHeader>

        <div className="px-8 py-6 space-y-6 max-h-[60vh] overflow-y-auto">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-stone-500 mb-3">Step 1 · Create the OAuth app</div>
            <ol className="space-y-2 text-sm text-stone-300 list-decimal list-outside pl-4" data-testid="wizard-steps">
              {meta.steps.map((s, i) => (
                <li key={i} className="pl-1 leading-relaxed">{s}</li>
              ))}
            </ol>
            <a href={meta.console_url} target="_blank" rel="noreferrer" data-testid="wizard-console-link"
               className="inline-flex items-center gap-1.5 mt-4 text-[10px] uppercase tracking-widest font-mono px-3 py-1.5 border border-stone-700 hover:border-orange-600 text-stone-200 hover:text-orange-500 transition-colors">
              Open {meta.label} console <ArrowSquareOut size={12} weight="bold" />
            </a>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-stone-500 mb-3">Step 2 · Redirect URI (paste this on the provider)</div>
            <div className="flex items-center gap-2 border border-stone-800 bg-stone-900 px-3 py-2.5">
              <code className="flex-1 text-xs font-mono text-stone-200 break-all" data-testid="wizard-redirect-uri">{redirectUri}</code>
              <CopyButton text={redirectUri} testid="wizard-copy-redirect" />
            </div>
            {meta.scopes_hint && (
              <div className="mt-2 text-[11px] font-mono text-stone-500">{meta.scopes_hint}</div>
            )}
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-stone-500 mb-3">Step 3 · Add to <span className="text-stone-300">/app/backend/.env</span></div>
            <div className="flex items-start gap-2 border border-stone-800 bg-stone-900 px-3 py-2.5">
              <pre className="flex-1 text-xs font-mono text-stone-200 whitespace-pre" data-testid="wizard-env-snippet">{envSnippet}</pre>
              <CopyButton text={envSnippet} testid="wizard-copy-env" />
            </div>
            <div className="mt-2 text-[11px] font-mono text-stone-500">
              After saving, restart the backend (or wait ~5s for autoreload) and click Retry connect below.
            </div>
          </div>
        </div>

        <DialogFooter className="px-8 py-5 border-t border-stone-800 flex flex-row gap-2">
          <button
            data-testid="wizard-close"
            onClick={() => onOpenChange(false)}
            className="text-[10px] uppercase tracking-widest font-mono px-4 py-2 border border-stone-700 text-stone-300 hover:border-stone-500 transition-colors"
          >
            Close
          </button>
          <button
            data-testid="wizard-retry"
            onClick={() => { onOpenChange(false); onRetry?.(); }}
            className="text-[10px] uppercase tracking-widest font-mono px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white transition-colors"
          >
            I&apos;ve added them — retry connect
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
